import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
import { z } from 'zod';
import { TypedMutationConfig } from '@/lib/react-query';
import { useMutation } from '@tanstack/react-query';

export const UpdateTicketValidationSchema = z.object({
  spaceId: z.string({ message: 'Space ID is required' }).min(1),
  ticketId: z.string({ message: 'Ticket ID is required' }).min(1),
  spaceColumnId: z.string().optional(),
  position: z.string().optional(),
  title: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  content: z.string().max(30000, 'Content must be at most 30000 characters').optional(),
  labelTicketIds: z.array(z.string()).optional(),
  userIds: z.array(z.string()).optional(),
  caseIds: z.array(z.string()).optional(),
  attachments: z
    .array(
      z.object({
        fileName: z.string(),
        size: z.number().optional().nullable(),
        uri: z.string(),
        contentType: z.string(),
      })
    )
    .optional(),
  isCompleted: z.boolean().optional(),
});
export type TUpdateTicketValidation = z.infer<typeof UpdateTicketValidationSchema>;
export type TUpdateTicketPayload = TUpdateTicketValidation;
export type TUpdateTicketResponse = ApiResponse<{
  message: string;
}>;

export const updateTicket = async (payload: TUpdateTicketPayload): Promise<TUpdateTicketResponse> => {
  const url = `/ticket/${payload.ticketId}`;

  const payloadData = {
    spaceColumnId: payload.spaceColumnId,
    position: payload?.position,
    title: payload?.title,
    startDate: payload?.startDate,
    endDate: payload?.endDate,
    content: payload?.content,
    labelTicketIds: payload?.labelTicketIds,
    userIds: payload?.userIds,
    caseIds: payload?.caseIds,
    attachments: payload?.attachments,
    isCompleted: payload?.isCompleted,
  };

  return axiosInstance.patch(url, payloadData);
};

/**
 * Use Edit Ticketing Space React-Query Hook
 */
type UseUpdateTicketMutationOptions = {
  mutationConfig?: TypedMutationConfig<typeof updateTicket, AxiosErrorCustom>;
};

/**
 * Use Edit Ticketing Space React-Query Hook
 */
export const useUpdateTicketMutation = ({ mutationConfig }: UseUpdateTicketMutationOptions) => {
  const { ...restConfig } = mutationConfig ?? {};

  return useMutation({
    mutationFn: updateTicket,
    ...restConfig,
  });
};
